import {
  CheckCircle2,
  CircleAlert,
  Clock,
  Cog,
  Image as ImageIcon,
  Inbox,
  MapPin,
  Package,
  PackageCheck,
  PauseCircle,
  Truck,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type { StatusTone } from '@/components/ui/statusTone';

interface StatusLook {
  tone: StatusTone;
  icon?: LucideIcon;
}

/**
 * Orders, shipments and items share one status vocabulary, so each status has one
 * look on every surface. Holds are amber and errors red: the tone is the severity,
 * and the label says which hold or error it is.
 */
const STATUS_LOOK: Record<string, StatusLook> = {
  open: { tone: 'green', icon: Inbox },
  fulfillmentready: { tone: 'green' },
  delivered: { tone: 'green', icon: PackageCheck },
  pending: { tone: 'amber', icon: Clock },
  personalizationhold: { tone: 'amber', icon: Clock },
  customercarehold: { tone: 'amber', icon: PauseCircle },
  hold: { tone: 'amber', icon: PauseCircle },
  manualhold: { tone: 'amber', icon: PauseCircle },
  fulfillmenthold: { tone: 'amber', icon: PauseCircle },
  readyforfulfillment: { tone: 'cyan', icon: Package },
  inproduction: { tone: 'teal', icon: Cog },
  shipped: { tone: 'teal', icon: Truck },
  intransit: { tone: 'teal', icon: Truck },
  fulfilled: { tone: 'blue', icon: CheckCircle2 },
  canceled: { tone: 'red', icon: XCircle },
  cancelled: { tone: 'red', icon: XCircle },
  rejected: { tone: 'red', icon: XCircle },
  rejectedbyvendor: { tone: 'red', icon: XCircle },
  imageerror: { tone: 'red', icon: ImageIcon },
  fulfillmenterror: { tone: 'red', icon: Truck },
  addresserror: { tone: 'red', icon: MapPin },
  addressissue: { tone: 'red', icon: MapPin },
  error: { tone: 'red', icon: CircleAlert },
};

/** Collapses the casings the services send (PascalCase, camelCase, snake_case) to one key. */
export function statusKey(status: string | null | undefined): string {
  return (status ?? '').toLowerCase().replace(/[^a-z]/g, '');
}

export function statusTone(status: string | null | undefined): StatusTone {
  return STATUS_LOOK[statusKey(status)]?.tone ?? 'gray';
}

export function statusIcon(status: string | null | undefined): LucideIcon | undefined {
  return STATUS_LOOK[statusKey(status)]?.icon;
}
